import React, { useMemo } from "react";

const roleColorMap = {
  admin: '#1E40AF',
  manager: '#2563EB',
  dispatcher: '#0EA5E9',
  freightbroker: '#06B6D4',
  bidagent: '#14B8A6',
  salesagent: '#10B981',
  'pre-dispatcher': '#22C55E',
  predispatcher: '#22C55E',
  accountingmanager: '#6366F1',
  accountingin: '#8B5CF6',
  accountingout: '#A855F7',
  partner: '#64748B',
  curtainvan: '#3B82F6',
  'carhauler/enclosedtrailer': '#60A5FA',
  carhauler: '#60A5FA',
  enclosedtrailer: '#60A5FA',
  flatbed: '#93C5FD',
  dryvan: '#818CF8',
  refrigerated: '#A78BFA',
  stepdeck: '#C4B5FD',
  lowboy: '#34D399',
  box: '#6EE7B7',
  other: '#A7F3D0'
};

const fallbackPalette = [
  '#3B82F6',
  '#60A5FA',
  '#93C5FD',
  '#DBEAFE',
  '#818CF8',
  '#A78BFA',
  '#C4B5FD',
  '#34D399',
  '#6EE7B7',
  '#A7F3D0'
];

const loadStatusColorMap = {
  delivered: '#22c55e',
  'picked up': '#3b82f6',
  pickedup: '#3b82f6',
  'picked-up': '#3b82f6',
  dispatched: '#fb923c',
  'on hold': '#f59e0b',
  onhold: '#f59e0b',
  'on-hold': '#f59e0b',
  cancelled: '#ef4444',
  canceled: '#ef4444',
  listed: '#6b7280',
  expired: '#dc2626'
};

const getRoleColor = (roleSlug) => {
  const normalizedRole = roleSlug.toLowerCase();
  if (roleColorMap[normalizedRole]) {
    return roleColorMap[normalizedRole];
  }
  
  const normalizedWithoutDashes = normalizedRole.replace(/-/g, '');
  if (roleColorMap[normalizedWithoutDashes]) {
    return roleColorMap[normalizedWithoutDashes];
  }
  
  let hash = 0;
  for (let i = 0; i < normalizedRole.length; i++) {
    hash = ((hash << 5) - hash) + normalizedRole.charCodeAt(i);
    hash = hash & hash;
  }
  const index = Math.abs(hash) % fallbackPalette.length;
  return fallbackPalette[index];
};

const getLoadStatusColor = (statusSlug) => {
  if (!statusSlug) return '#64748b';
  
  const normalizedStatus = String(statusSlug).toLowerCase().trim();
  
  if (loadStatusColorMap[normalizedStatus]) {
    return loadStatusColorMap[normalizedStatus];
  }
  
  const normalizedWithoutDashes = normalizedStatus.replace(/-/g, '').replace(/\s+/g, '');
  if (loadStatusColorMap[normalizedWithoutDashes]) {
    return loadStatusColorMap[normalizedWithoutDashes];
  }
  
  const normalizedWithSpaces = normalizedStatus.replace(/-/g, ' ');
  if (loadStatusColorMap[normalizedWithSpaces]) {
    return loadStatusColorMap[normalizedWithSpaces];
  }
  
  return '#64748b';
};

const extractCount = (label) => {
  const match = label.match(/\((\d+)\)/);
  return match ? parseInt(match[1], 10) : 0;
};

const extractRoleName = (label) => {
  return label.replace(/\s*\(\d+\)$/, '');
};

const isPaymentFormatLabel = (label) => /\d+,\s*\$/.test(label);

const UsersDonutCard = ({ total, segments = [], title = "Users", totalLabel = "Total" }) => {
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const totalValue = Number(total) || 0;

  const isLoadsDonut = title.toLowerCase().includes('loads by status') || 
                       (title.toLowerCase().includes('load') && title.toLowerCase().includes('status'));

  const roleSegments = useMemo(() => {
    const mappedSegments = segments.map((segment) => {
      const isPaymentFormat = isPaymentFormatLabel(segment.label);
      const count = segment.count ?? extractCount(segment.label);
      const role = segment.slug;
      const fill = isLoadsDonut ? getLoadStatusColor(role) : getRoleColor(role);
      return {
        role,
        count,
        fill: fill || '#64748b',
        label: isPaymentFormat ? segment.label : extractRoleName(segment.label),
        value: segment.value,
        showCount: !isPaymentFormat
      };
    });
    return mappedSegments;
  }, [segments, isLoadsDonut]);

  let currentOffset = 0;

  const renderSegments = () =>
    roleSegments.map((seg, index) => {
      const dasharray = (seg.value / 100) * circumference;
      if (dasharray <= 0) {
        return null;
      }
      const circle = (
        <circle
          key={seg.role}
          className="donut-segment"
          r={radius}
          cx="100"
          cy="100"
          strokeDasharray={`${dasharray} ${circumference}`}
          strokeDashoffset={currentOffset}
          style={{ stroke: seg.fill }}
        />
      );

      currentOffset -= dasharray;
      return circle;
    });

  return (
    <div className="chart-card users-donut-card">
      <div className="chart-card__title-row">
        <span className="chart-card__title">{title}</span>
      </div>

      <div className="users-donut-card__content">
        <div className="users-donut-card__chart">
          <svg viewBox="0 0 200 200">
            <circle className="donut-track" r={radius} cx="100" cy="100" />
            {renderSegments()}
            <text x="50%" y="50%" textAnchor="middle" className="donut-total">
              {totalLabel}
            </text>
            <text x="50%" y="62%" textAnchor="middle" className="donut-total-value">
              {totalValue.toLocaleString()}
            </text>
          </svg>
        </div>

        <div className="users-donut-card__legend">
          {roleSegments.map((seg) => (
            <div className="users-donut-card__legend-item" key={seg.role}>
              <span 
                className={`legend-dot legend-dot-${seg.role}`}
                style={{ backgroundColor: seg.fill }}
              />
              <span className="users-donut-card__legend-role">{seg.label}</span>
              {seg.showCount && <span className="users-donut-card__legend-count">{seg.count}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UsersDonutCard;

